import { getForeignId, setForeignId } from '@/utils/objUtil'
import { cloneDeep, isEqual, isObject, omit, pick } from 'lodash-es'
import { Cp } from './Contract'
import { MATERIAL_ENUM, MATERIAL_FILE_ENUM } from '@/views/cpTask/const'

export const defaultAudioBase = () => {
  return {
    insertType: 1, // 上架方式，1人工
    batchAuthType: 1, // 授权类型
    paymentModeId: undefined, // 付费模式
    paymentTransTime: '', // 付费转库时间
    songsCnt: 0, // 歌曲数量
    kTrackCnt: 0, // 伴奏数量
    mvCnt: 0, // mv数量
  }
}

// 物料批次对象
export const defaultAudioBatchObj = (type = 1) => {
  return {
    id: null,
    remarks: '', // 批次物料备注
    sourceType: type, // 物料类型
    trackFileList: [], // 物料文件
    wanpanUrl: '',
    authFileList: [], // 物料文件
    songlistFileList: [], // 物料文件
    singerName: '', // 艺人名
    onlineTime: '', // 上线时间
    replaceAudioList: [] // 替换流水
  }
}

/* ============= CpTask ============= */
export class CpTask extends Cp {
  // 前端私有处理字段
  _contractKeys = [] // 合同字段列表
  _oldData = null // 未修改前的对象

  id = null
  buyStatus = 0
  audioCode = 0 // 歌曲进度
  cpCode = 0 // 版权进度
  taskName = ''
  buyId = '' // 采买任务ID
  buyType = 0 // 采买类型
  authorizer = ''
  disCompanyId = '' // 外显厂牌方
  contentType // 内容组别
  contractType // 签约类型
  audioBase = defaultAudioBase()
  trackReviewInfo = null // 歌曲审核
  cpReviewInfo = null // 合同审核
  audioInfo = [defaultAudioBatchObj(MATERIAL_ENUM.TRACK.code)] // 歌曲物料文件列表
  mvInfo = [defaultAudioBatchObj(MATERIAL_ENUM.MV.code)] // mv物料文件列表
  isAudioChange = true // 物料是否变更
  isCpChange = true // 版权是否变更
  modifyRecords = {} // 修改记录列表
  commentList = [] // 备注评论列表
  comment = '' // 当前任务填写的备注
  isValid = 1 // 任务运行状态 0=停用，1=启用
  createUser = '' // 任务创建人
  isSkipCpCheck = 0 // 是否跳过版权审核

  constructor(initObj) {
    super()

    this._contractKeys = this._getProperties()
    this._collectProperties()
    if (initObj) {
      this._setValues(initObj)
      this.init(initObj)
    }

    // 保存未修改的文件
    this._oldData = Object.freeze(cloneDeep(this))
  }

  init(initObj) {
    // 调用父类处理合同相关字段
    super.init(initObj)
    // 处理采买任务
    getForeignId(this, 'buyId', 'id', 'taskName')
    // 处理外显厂牌
    getForeignId(this, 'disCompanyId', 'companyId', 'companyName')

    // 处理批次物料列表
    this.audioInfo = parseAudioFile(initObj.audioInfo)
    this.mvInfo = parseAudioFile(initObj.mvInfo)
  }

  /**
   *
   * @override
   * @memberof CpTask
   */
  getBackendData() {
    const postData = super.getBackendData()

    // 计算版权是否变更
    postData.isCpChange = this.isContractChange()
    // 计算物料文件是否变更
    postData.isAudioChange = this.isAudioOrMvChange() || this.isPaymentChange()

    // 处理采买任务
    setForeignId(postData, 'buyId', 0)
    // 处理外显厂牌厂牌
    setForeignId(postData, 'disCompanyId', 0)

    // 处理批次物料列表
    postData.audioInfo = audioFileToBackend(postData.audioInfo)
    postData.mvInfo = audioFileToBackend(postData.mvInfo)

    delete postData._oldData
    delete postData._contractKeys
    return postData
  }

  isContractChange() {
    const omitKeys = ['_contractKeys', 'remarkShow', 'autoWord', 'autoMusic', 'wmRelate']
    return !isObjectEqual(this._oldData, this, this._contractKeys, omitKeys)
  }

  isPaymentChange() {
    const paymentKeys = ['paymentModeId', 'paymentTransTime']
    return !isObjectEqual(this._oldData.audioBase, this.audioBase, paymentKeys)
  }

  isAudioOrMvChange() {
    // 暂不处理 mvInfo
    const formatAudioInfo = parseDiffInfo(this.audioInfo)
    return !isObjectEqual(this._oldData.audioInfo, formatAudioInfo, Object.keys(formatAudioInfo))
  }
}

// 同一批次物料根据文件类型拆分
function parseAudioFile(audioList) {
  if (!audioList) return []

  return audioList.map(audio => {
    const newAudio = cloneDeep(audio)
    newAudio.fileList = newAudio.fileList.map(file => {
      file.url = file.fileUrl
      file.name = file.fileName
      return file
    })
    newAudio.trackFileList = newAudio.fileList.filter(f => f.fileType === MATERIAL_FILE_ENUM.TRACK.code) // 歌曲文件
    newAudio.wanpanUrl = newAudio.fileList.find(f => f.fileType === MATERIAL_FILE_ENUM.WNGPAN.code)?.url || '' // 物料网盘链接
    newAudio.authFileList = newAudio.fileList.filter(f => f.fileType === MATERIAL_FILE_ENUM.AUTH.code) // 授权文件
    newAudio.songlistFileList = newAudio.fileList.filter(f => f.fileType === MATERIAL_FILE_ENUM.SONGLIST.code) // 歌单信息
    newAudio.replaceAudioList = newAudio.replaceAudioList ?? []
    delete newAudio.fileList

    return newAudio
  })
}

/**                                    后台相关处理函数                                         */
function addFileType(fileList, type) {
  return fileList.map(file => {
    file.fileType = type
    return file
  })
}

// 处理物料文件列表
function audioFileToBackend(audioList) {
  if (!audioList) return []

  return audioList.map(audio => {
    const newAudio = cloneDeep(audio)
    const { trackFileList, wanpanUrl, authFileList, songlistFileList, ...rest } = newAudio
    const wangpanFileList = wanpanUrl ? [{
      fileName: '',
      fileUrl: wanpanUrl,
    }] : []
    rest.fileList = [
      ...addFileType(trackFileList, MATERIAL_FILE_ENUM.TRACK.code),
      ...addFileType(wangpanFileList, MATERIAL_FILE_ENUM.WNGPAN.code),
      ...addFileType(authFileList, MATERIAL_FILE_ENUM.AUTH.code),
      ...addFileType(songlistFileList, MATERIAL_FILE_ENUM.SONGLIST.code),
    ]

    return rest
  }).filter(v => !!v.fileList.length || v.id)
}

/**
 * 递归剔除嵌套对象的属性
 *
 * @param {*} data 操作对象
 * @param {*} keys 需要剔除的属性
 * @returns {Object}
 */
function omitDeep(data, keys = []) {
  if (!keys || !Array.isArray(keys) || !keys.length) return cloneDeep(data)

  const result = omit(data, keys)
  for (const i in result) {
    const val = result[i]
    if (isObject(val)) result[i] = omitDeep(val, keys)
  }

  return result
}

function parseDiffInfo(info) {
  const formatInfo = cloneDeep(info).filter(v => {
    // 过滤无效的物料对象，依据为没有上传任何物料且 id 为 null
    return v.id || v.wanpanUrl || v.authFileList.length || v.trackFileList.length || v.songlistFileList.length
  }).map(audioObj => {
    // el-upload 组件会增加组件内部使用的字段，避免 diff 到这些新增字段，需要删除
    const omitKeys = ['uid', 'status']
    audioObj.trackFileList = omitDeep(audioObj.trackFileList, omitKeys)
    audioObj.authFileList = omitDeep(audioObj.authFileList, omitKeys)
    audioObj.songlistFileList = omitDeep(audioObj.songlistFileList, omitKeys)
    audioObj.replaceAudioList = audioObj.replaceAudioList.map(item => {
      item.fileList = omitDeep(item.fileList, omitKeys)
      return item
    })
    return audioObj
  })

  return formatInfo
}

/**
 * 比较两个对象是否相等
 *
 * @param {*} data1 旧对象
 * @param {*} data2 修改后的对象
 * @param {*} compareKeys 对象中需要比较的keys
 * @param {*} omitKeys 对象中需要剔除的keys，之所以单独传是因为可能有嵌套对象中的属性需要剔除
 * @returns {Boolean}
 */
function isObjectEqual(data1, data2, compareKeys = [], omitKeys = []) {
  const a = omitDeep(pick(data1, compareKeys), omitKeys)
  const b = omitDeep(pick(data2, compareKeys), omitKeys)

  return isEqual(a, b)
}
